import "bootstrap/dist/css/bootstrap.min.css";
import {useEffect, useRef, useState} from "react";
import { Link, Route, Routes, useParams } from "react-router-dom";
import { HubConnectionBuilder } from "@microsoft/signalr";
import { useLocation } from "react-router-dom";
import "./App.css";
import Player from "./components/Player";
import Game from "./components/Game";
import NewGame from "./components/NewGame";
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import Container from 'react-bootstrap/Container';
import ConnectionModal from "./components/ConnectionModal";

// US1: Custom reconnect policy — exponential backoff, capped at 60s, never gives up.
// Default policy only retries 4 times (0s, 2s, 10s, 30s) then permanently disconnects.
const reconnectPolicy = {
    nextRetryDelayInMilliseconds(retryContext) {
        if (retryContext.previousRetryCount === 0) return 0;
        return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 60000);
    }
};

function App() {
    const [ connection, setConnection ] = useState(null);
    const [ game, setGame ] = useState([]);
    const [ games, setGames ] = useState([]);
    const [ player, setPlayer ] = useState({});
    const [ requestedGameId, setRequestedGameId ] = useState(null);
    const [ turn, setTurn ] = useState({});
    const [ expanded, setExpanded ] = useState(false);
    const [ connectionStatus, setConnectionStatus ] = useState("connected");
    const location = useLocation();
    const locationRef = useRef(location.pathname);
    locationRef.current = location.pathname;
    let gameId = location.pathname.toLowerCase().includes("game") ? location.pathname.split("/")[2] : null || null;
    // console.log("LOCATION", location, gameId)

    useEffect(() => {
        // get identity
        fetch("/api/environment")
        .then(response => {
            console.log("RESPONSE STATUS", response.ok)
            if (response.ok) {
                return response.json()
            } else {
                console.log(response)
                // window.location.assign(`/Identity/Account/Login?returnUrl=${window.location.pathname}`);
            }
        })
        .then(environment => {
            if (environment) {
                setPlayer(environment);
                console.log("LOGGED IN PLAYER", environment);
                const newConnection = new HubConnectionBuilder()
                .withUrl('/crabbleshub')
                .withAutomaticReconnect(reconnectPolicy)
                .build();

                setConnection(newConnection);
                console.log("CONNECTION SET")
            }
        })
        
    }, []);

    useEffect(() => {
        if (connection) {
            // US3: match server ClientTimeoutInterval=60s
            connection.serverTimeoutInMilliseconds = 60000;
            connection.start()
                .then(result => {
                    console.log('Connected!', connection);
    
                    connection.on('Test', (sender, message) => {
                        console.log("RECEIVED TEST", sender, message);
                    });

                    // US1: lifecycle callbacks — drive ConnectionModal state
                    connection.onreconnecting(() => {
                        setConnectionStatus("reconnecting");
                    });

                    connection.onreconnected(() => {
                        setConnectionStatus("connected");
                        // Re-register with GameConnectionTracker and refresh stale state.
                        const currentPath = locationRef.current;
                        if (currentPath.toLowerCase().includes("game")) {
                            const gid = currentPath.split("/")[2];
                            if (gid) connection.invoke("GetGame", parseInt(gid), player.id);
                        } else {
                            connection.invoke("GetGames", player.id);
                        }
                    });

                    connection.onclose(() => {
                        setConnectionStatus("disconnected");
                    });


                    connection.on("Games", games => {
                        console.log("GAMES RECEIVED")
                        console.log("RECEIVED GAMES", games);
                        setGames(games)
                    });                    connection.on("Game", game => {
                        console.log("GAME RECEIVED")
                        console.log("RECEIVED GAME", game);
                        setGame(game);
                        if (locationRef.current === "/") {
                            getGames();
                        }
                    });
                    
                    connection.on("Scoring", receivedTurn => {
                        console.log("TURN RECEIVED")
                        console.log(`RECEIVED TURN`, receivedTurn);
                        setTurn(receivedTurn)
                    })

                    connection.on("RackSaved", rack => {
                        console.log("RACK SAVED")
                        console.log("RECEIVED RACK", rack);
                    }
                    );
                    // US1: removed dead "Error" listener — hub sends "BackendError", not "Error".
                    if (gameId) {
                         getGame(gameId)
                    } else {
                        connection.invoke("GetGames", player.id);
                    }
                    
                    if (requestedGameId) {
                        console.log("REQUESTING GAME AFTER CONNECTED", requestedGameId, player.id);
                        // connection.invoke("GetGameForPlayer");
                        connection.invoke("GetGame", parseInt(requestedGameId), player.id);
                        setRequestedGameId(null);
                    }
                })
                .catch(e => console.log('Connection failed: ', e));
        }
    }, [connection]);

    const normalizeEmail = (email) => (email ? email.trim().toLowerCase() : "");

    const normalizeCreatePlayers = (rawPlayers) => {
        const creator = {
            id: player.id,
            name: player.name,
            email: normalizeEmail(player.email)
        };

        const participantCandidates = Array.isArray(rawPlayers) ? rawPlayers : [];
        const seen = new Set();
        const additionalPlayers = [];

        for (const participant of participantCandidates) {
            if (!participant) {
                continue;
            }

            const id = Number(participant.id || 0);
            const email = normalizeEmail(participant.email);
            const isCreatorById = id > 0 && id === creator.id;
            const isCreatorByEmail = email && email === creator.email;

            if (isCreatorById || isCreatorByEmail) {
                continue;
            }

            if (id <= 0 && !email) {
                continue;
            }

            const key = id > 0 ? `id:${id}` : `email:${email}`;
            if (seen.has(key)) {
                continue;
            }

            seen.add(key);
            additionalPlayers.push({
                ...participant,
                id,
                email,
            });

            if (additionalPlayers.length === 3) {
                break;
            }
        }

        return [creator, ...additionalPlayers];
    };

    const normalizeCreateOptions = (rawOptions) => {
        return {
            name: rawOptions?.name?.trim() || "",
            isPublic: Boolean(rawOptions?.isPublic),
        };
    };

    const createGame = (options, players) => {
        const normalizedOptions = normalizeCreateOptions(options);
        const normalizedPlayers = normalizeCreatePlayers(players);
        console.log("Creating game from App.js", normalizedOptions, normalizedPlayers);
        return connection.invoke("CreateGame", normalizedOptions, normalizedPlayers);
    }

    const getGames = () => {
        if (connection && connection.state === "Connected") {
            connection.invoke("GetGames", player.id);
        }
    }

    const getGame = (gameId) => {
        if (connection && connection.state === "Connected") {
            console.log("REQUESTING GAME", gameId);
            connection.invoke("GetGame", parseInt(gameId), player.id);
        } else {
            setRequestedGameId(gameId);
        }
    }

    const saveRack = (gameId, playerIndex, rack) => {
        console.log("INVOKING SAVE RACK", gameId, playerIndex, rack);
        if (connection && connection._connectionState == "Connected") {
            connection.invoke("SaveRack", parseInt(gameId), playerIndex, rack);
        }
    }

    const deleteGame = (gameId) => {
        connection.invoke("DeleteGame", gameId, player.id);
    }

    const undeleteGame = (gameId) => {
        connection.invoke("UndeleteGame", gameId, player.id);
    }

    const destroyGame = (gameId) => {
        connection.invoke("DestroyGame", gameId, player.id);
    }

    const copyGame = (gameId) => {
        connection.invoke("CopyGame", gameId, player.id);
    }

    return (
        <div className={`container${location.pathname.includes("game") ? "-fluid" : ""}`} style={{padding: 0}}>
            <ConnectionModal status={connectionStatus} />
            <Navbar expanded={expanded} className="bg-body-tertiary">
                <Container>
                    <Navbar.Brand><a className="text-decoration-none" href="/">Crabbles</a></Navbar.Brand>
                    <Navbar.Toggle onClick={() => setExpanded(expanded ? false : true)} aria-controls="basic-navbar-nav" />
                    <Navbar.Collapse id="basic-navbar-nav">
                        <Navbar.Text><Link onClick={() => setExpanded(false)} className="text-decoration-none" to="/">My Games</Link></Navbar.Text>
                    </Navbar.Collapse>
                    <Navbar.Text><Link className="text-decoration-none" to="/profile">{player?.name}</Link></Navbar.Text>
                </Container>
            </Navbar>
            <div style={{position: "relative"}}>
            <Routes>
                <Route exact path="/" element={<Player games={games} player={player} getGames={getGames} deleteGame={deleteGame} undeleteGame={undeleteGame} destroyGame={destroyGame} copyGame={copyGame} connectionStatus={connectionStatus} />} />
                <Route path="/game/:gameId" element={<Game getGame={getGame} saveRackToGame={saveRack} connection={connection} game={game} setGame={setGame} turn={turn} player={player} connectionStatus={connectionStatus} />} />
                <Route path="/newgame" element={<NewGame player={player} createGame={createGame} />} />
            </Routes>
        </div>
        </div>
    );
}

export default App;
